"use client";

import Image from "next/image";
import GrowWealthChevron from "@/app/assets/icons/grow-wealth/chevron-right";
import Breadcrumb, { type BreadcrumbPath } from "@/app/components/breadcrumb";
import TrackedLink from "@/app/components/icm 3.0/shared/TrackedLink";
import { InvestmentOptions } from "@/app/interface/platform";
import { StocksAssets } from "@/app/stocks/icm 3.0/stocks-assets";
import {
    // BrokingChartLineIcon,
    BrokingHeroStars,
    BrokingHeroWaveMobile,
    BrokingHeroWaveWeb,
    BrokingShieldCheckIcon,
    TrendingDownIcon,
    TrendingUpIcon,
} from "@/app/assets/icons/broking-hero/icons";

const stocksBreadcrumbPaths: BreadcrumbPath[] = [
    { label: "Home", route: "/" },
    { label: InvestmentOptions.Stocks },
];

const PhoneReel = ({
    className,
    width,
    height,
}: {
    className: string;
    width: number;
    height: number;
}) => (
    <Image
        src={StocksAssets.heroSaveBrokerage}
        alt="InCred Money Broking save brokerage"
        width={width}
        height={height}
        className={className}
        unoptimized
        priority
    />
);

export type MarketTickerItem = {
    name: string;
    change: string;
    direction: "up" | "down";
};

const MarketTicker = ({ items }: { items?: MarketTickerItem[] }) => {
    if (!items || items.length === 0) return null;

    const loop = [...items, ...items];

    return (
        <div className="w-full overflow-hidden" aria-label="Market indices">
            <div className="animate-broking-ticker flex w-max items-center gap-6">
                {loop.map((item, i) => (
                    <div
                        key={`${item.name}-${item.change}-${i}`}
                        className="flex shrink-0 items-center gap-1"
                    >
                        <p className="whitespace-nowrap text-sm font-semibold leading-5 text-black">
                            {item.name}
                        </p>
                        <div className="flex h-5 items-center gap-1">
                            {item.direction === "up" ? <TrendingUpIcon /> : <TrendingDownIcon />}
                            <p
                                className={`whitespace-nowrap text-sm font-semibold leading-5 ${
                                    item.direction === "up" ? "text-[#0DCC54]" : "text-[#FF0303]"
                                }`}
                            >
                                {item.change}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TrustRow = ({ stacked }: { stacked?: boolean }) => (
    <div className="flex flex-col items-start justify-center gap-3 md:gap-5">
        <div className="flex items-center gap-2">
            <BrokingHeroStars />
            <p className="whitespace-nowrap text-sm font-semibold leading-5 text-black">
                4.9 stars, 8.28k Reviews
            </p>
        </div>
        <div className={`flex items-start gap-5 ${stacked ? "flex-col" : "flex-row items-center"}`}>
            <div className="flex items-center gap-2 text-black">
                <BrokingShieldCheckIcon />
                <p className="whitespace-nowrap text-sm font-semibold leading-5 text-black">
                    SEBI REGISTERED
                </p>
            </div>
            {/* <div className="flex items-center gap-2">
                <BrokingChartLineIcon />
                <p className="whitespace-nowrap text-sm font-semibold leading-5 text-black">
                    ₹1,350 CR+ ASSETS MANAGED
                </p>
            </div> */}
        </div>
    </div>
);

/**
 * Broking hero — Figma desktop 1916:1086 / mobile 2541:35970
 * Nav is fixed and overlays this section (same as homepage).
 * Mobile height = Figma 828 + fixed nav offset (82).
 */
const HeroSection = ({ tickerItems }: { tickerItems?: MarketTickerItem[] }) => {
    return (
        <section
            aria-label="Trade More. Pay Less."
            className="relative h-[910px] w-full overflow-hidden bg-white md:h-[865px] md:bg-[#F5F5F5]"
        >
            <h1 className="sr-only">Trade More. Pay Less.</h1>

            {/* Waves */}
            <div
                className="pointer-events-none absolute top-[calc(-47%-65.5px)] hidden h-[1970px] w-[1957px] md:block"
                aria-hidden
            >
                <BrokingHeroWaveWeb />
            </div>
            <div
                className="pointer-events-none absolute left-[calc(50%+83px)] top-[calc(50%-1px)] h-[1080px] w-[1043px] -translate-x-1/2 -translate-y-1/2 md:hidden"
                aria-hidden
            >
                <BrokingHeroWaveMobile className="size-full" />
            </div>

            {/* ── Desktop (1440 artboard) ── */}
            <div className="relative mx-auto hidden h-full w-full max-w-[1440px] md:block">
                <div className="absolute left-[120px] top-[130px] z-10 h-5 w-[1192px]">
                    <MarketTicker items={tickerItems} />
                </div>

                <div className="absolute left-[120px] top-[166px] z-10 w-[1192px]">
                    <Breadcrumb paths={stocksBreadcrumbPaths} />
                </div>

                <div className="absolute left-[120px] top-[287px] z-10 flex w-[643px] flex-col items-start gap-[35px]">
                    <div className="flex w-full flex-col items-start gap-10">
                        <div className="flex w-full flex-col items-start gap-6">
                            <p aria-hidden className="flex w-full flex-col items-start gap-2">
                                <span className="whitespace-nowrap text-[60px] font-bold leading-[73px] text-black">
                                    Trade More.
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="whitespace-nowrap text-[60px] font-bold leading-[73px] text-black">
                                        Pay {" "}
                                    </span>
                                    <span className="whitespace-nowrap text-[60px] font-bold leading-[73px] text-brand-primary-500">
                                        Less.
                                    </span>
                                </span>
                            </p>
                            <p className="w-[612px] text-2xl font-regular leading-8 text-[#767676]">
                                InCred Money Broking combines lightning-fast execution, advanced
                                trading tools, and flat-fee pricing—so you can focus on
                                opportunities, not costs.
                            </p>
                        </div>

                        <TrackedLink
                            href="/login"
                            event="Broking_Get_Started_Fold1"
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-[var(--radius-m)] bg-cta-primary px-4 py-2 text-sm font-medium leading-5 text-cta-primary-text no-underline transition-colors hover:bg-cta-primary-hover hover:text-cta-primary-text-hover"
                        >
                            Start Investing
                            <span className="text-cta-primary-icon">
                                <GrowWealthChevron />
                            </span>
                        </TrackedLink>
                    </div>

                    <TrustRow />
                </div>

                <div className="absolute left-[952px] top-[205px] z-10 h-[630px] w-[375px] overflow-hidden">
                    <PhoneReel
                        className="h-[630px] w-full object-cover object-top"
                        width={274}
                        height={630}
                    />
                </div>
            </div>

            {/* ── Mobile (393 artboard) — Figma 2541:35970 ── */}
            <div className="relative mt-[82px] h-full md:hidden">
                <div className="absolute left-4 top-2 z-10 w-[363px]">
                    <Breadcrumb paths={stocksBreadcrumbPaths} />
                </div>

                <div className="absolute left-4 top-[72px] z-10 flex w-[363px] flex-col items-start gap-6">
                    <div className="flex w-[346px] flex-col items-start gap-4">
                        <p aria-hidden className="flex w-full flex-col items-start gap-2">
                            <span className="w-full text-[30px] font-bold leading-9 text-black">
                                Trade More.
                            </span>
                            <span className="flex w-full items-center gap-2">
                                <span className="whitespace-nowrap text-[30px] font-bold leading-9 text-black">
                                    Pay{" "}
                                </span>
                                <span className="whitespace-nowrap text-[30px] font-bold leading-9 text-brand-primary-500">
                                    Less.
                                </span>
                            </span>
                        </p>
                        <p className="w-full text-base font-regular leading-[22px] text-[#767676]">
                            InCred Money Broking combines lightning-fast execution, advanced trading tools, 
                            and flat-fee pricing—so you can focus on opportunities, not costs.
                        </p>
                    </div>

                    <TrustRow stacked />
                </div>

                <div className="absolute left-1/2 top-[315px] z-10 h-[500px] w-[315px] -translate-x-1/2 overflow-hidden">
                    <PhoneReel
                        className="h-full w-full object-cover object-top"
                        width={175}
                        height={402}
                    />
                </div>
            </div>
        </section>
    );
};

export default HeroSection;